import '@glideapps/glide-data-grid/dist/index.css'
import './Grid.css'

import { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { renderToString } from 'react-dom/server'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import {
  CellClickedEventArgs,
  DataEditor,
  EditableGridCell,
  GetRowThemeCallback,
  GridCell,
  GridCellKind,
  GridColumnIcon,
  GridMouseEventArgs,
  Item,
  Rectangle,
  SpriteMap,
} from '@glideapps/glide-data-grid'

import { IAddColumnBody } from '@makyoapp/makyo-sdk/api/Tables'
import { ColumnTypes } from '@makyoapp/makyo-sdk/models/Table'
import { ColumnAdapter, isEmail } from '@makyoapp/makyo-common'
import { getSystemTheme, useTheme } from '@makyoapp/makyo-ui'

import { isValidCoordinate } from '@/core/models/columns/helpers/coordinate'
import { cellBoundsAtom, jotaiStore } from '@/lib/jotai'

import { GridAddColumnMenu } from './components/grid/GridAddColumnMenu'
import { GridColumnMenu } from './components/grid/GridColumnMenu'
import { themesMap } from './components/grid/GridThemes'
import { GridSearchV2 } from './components/GridSearchV2'
import { GridModals } from './components/modal/GridModals'
import { GridPagination } from './components/pagination/GridPagination'
import { CustomGridRenderers } from './constants'
import { HeadersIconMap } from './constants/header-icons'
import { useGridV2Context } from './contexts/GridV2Context'
import { getCellContent } from './helpers/get-cell-content'

const ROW_HEIGHT = 34

/**
 * New Grid V2
 *
 * @todo add GridModal
 */
export function GridV2() {
  // Global State
  const { t } = useTranslation()

  // Theme State
  const { state: themeState } = useTheme()
  const currentColorTheme = themeState.mode === ('system' as any) ? getSystemTheme() : themeState.mode
  const currentTheme = themesMap[currentColorTheme]

  // Local State
  const [hoverRow, setHoverRow] = useState<number | undefined>(undefined)

  // Context State
  const {
    activeTable,
    dataEditorRef,
    handleGridFocus,
    columns,
    rows,
    filteredRowsCount,
    isLoading,
    glideColumns,
    combinedColumns,
    setGridModal,
    selection,
    setSelection,
    loadingAddRow,
    handleEditCell,
    handleAddRow,
    handleResizeColumn,
    handleReorderColumn,
    pagination,
    handlePaginationChange,
    totalPages,
    table,
    handleUpdateColumnSettings,
  } = useGridV2Context()

  const isSnapshot = table?.isSnapshot
  const isQuery = table?.isQuery

  const freezeColumnsLength = glideColumns?.filter((c) => c.isFrozen).length

  /* 
    Handle hover event on grid items: override row theme when hovered
  */
  const onItemHovered = useCallback((args: GridMouseEventArgs) => {
    const [_, row] = args.location

    setHoverRow(args.kind !== 'cell' ? undefined : row)
  }, [])

  const getRowThemeOverride = useCallback<GetRowThemeCallback>(
    (row) => {
      if (row !== hoverRow || hoverRow === rows.length) return undefined

      return {
        bgCell: currentTheme.cellHover,
      }
    },
    [currentTheme.cellHover, hoverRow, rows.length]
  )

  /**
   * Inline header edit state
   */
  const gridWrapperRef = useRef<HTMLDivElement>(null)
  const headerInputRef = useRef<HTMLInputElement>(null)
  const [headerEdit, setHeaderEdit] = useState<{
    colIdx: number
    colId: string
    value: string
    bounds: Rectangle
  } | null>(null)

  useEffect(() => {
    if (headerEdit) {
      const id = requestAnimationFrame(() => headerInputRef.current?.focus())
      return () => cancelAnimationFrame(id)
    }
  }, [headerEdit])

  const commitHeaderRename = useCallback(
    async (newName: string) => {
      if (!headerEdit || !table) return
      const trimmed = (newName || '').trim()
      if (!trimmed) {
        setHeaderEdit(null)
        return
      }

      const currentColumn = combinedColumns.find((c) => c.id === headerEdit.colId) as ColumnAdapter | undefined
      if (!currentColumn) {
        setHeaderEdit(null)
        return
      }

      if (currentColumn.name === trimmed) {
        setHeaderEdit(null)
        return
      }

      const body: IAddColumnBody = {
        _id: currentColumn.id,
        _Name: trimmed,
        _Type: currentColumn.type,
        _Settings: currentColumn.settings as any,
        _Connection: {
          _LocalColumnID: currentColumn.connections?.colId || '',
          _ForeignTableID: currentColumn.connections?.fTableId || '',
          _ForeignColumnID: currentColumn.connections?.fColId || '',
          _ForeignColumnIDToShow: currentColumn.connections?.fColIdToShow || '',
          _MatchMultiple: currentColumn.connections?.matchMultiple || false,
        },
        _Lookup: {
          _ForeignColumnIDToShow: currentColumn.lookup?.colId || '',
          _ConnectionColumnID: currentColumn.lookup?.connColId || '',
          _Standard: currentColumn.lookup?.standard || false,
        },
      }

      try {
        await handleUpdateColumnSettings(table.id, currentColumn.id, body)
      } finally {
        setHeaderEdit(null)
      }
    },
    [headerEdit, table, combinedColumns, handleUpdateColumnSettings]
  )

  /**
   * Grid cell content map. We use this to define the display of each cell based on its column type and settings
   */
  const handleGetCellContent = useCallback(
    ([col, row]: Item) => {
      let glideCol = glideColumns[col]
      let rowData = rows[row]

      if ((rowData && glideCol?.id === '_id') || glideCol?.id === 'session-id') {
        const rowIdValue = rowData._id
        return {
          kind: GridCellKind.RowID,
          data: rowIdValue,
          displayData: rowIdValue,
          allowOverlay: false,
        } as GridCell
      }

      if (glideCol && rowData) {
        let columnId = glideCol.id
        const column = combinedColumns.find((c) => c.id === columnId)! as ColumnAdapter

        if (column) {
          let cellContent = getCellContent(column, rowData, themeState.mode, dataEditorRef, Boolean(isSnapshot))
          return cellContent
        }
      }

      return { kind: GridCellKind.Loading } as GridCell
    },
    [glideColumns, rows, combinedColumns, themeState.mode, dataEditorRef, isSnapshot]
  )

  /**
   * @todo docs
   */
  const headerIcons = useMemo(() => {
    const fgColor = currentColorTheme === 'dark' ? '#fff' : '#000'

    return Object.entries(HeadersIconMap).reduce((acc, [columnType, Icon]) => {
      return {
        ...acc,
        [columnType]: () => renderToString(<Icon style={{ fill: fgColor }} width={14} height={14} />),
      }
    }, {}) as SpriteMap
  }, [currentColorTheme])

  /**
   * Grid Methods: Menu Header Click
   */
  const handleHeaderMenuClick = useCallback(
    (colIdx: number, bounds: Rectangle) => {
      const currentColumn = combinedColumns.find((col) => col.id === glideColumns[colIdx].id)

      if (currentColumn) {
        setGridModal((prev) => {
          return {
            isOpen: !prev?.isOpen,
            column: currentColumn as ColumnAdapter,
            bounds,
          }
        })
      }
    },
    [combinedColumns, glideColumns, setGridModal]
  )

  /**
   * Grid Method: Cell Clik
   */
  const handleCellClicked = useCallback(
    (cell: Item, event: CellClickedEventArgs) => {
      const rect = event.bounds
      jotaiStore.set(cellBoundsAtom, { x: rect.x, y: rect.y, width: rect.width, height: rect.height })
      handleGridFocus()
      // end handling tooltip when cell clicked
    },
    [handleGridFocus]
  )

  const handleVisibleRegionChanged = useCallback(() => {
    // hideTooltip()
  }, [])

  const columnTypeMap = useMemo(() => new Map(columns.map((col) => [col.id, col.type])), [columns])

  /**
   * @todo figure out if this fn is still necessary
   */
  const validateInput = useMemo(() => {
    const phonePattern = /^[+]?[()\d+\-\s.#*]+$/

    return (cell: Item, newValue: EditableGridCell) => {
      const [col] = cell

      const colId = glideColumns[col].id
      if (!colId) return false

      const colType = columnTypeMap.get(colId) || ColumnTypes.Text
      const strValue = newValue.data?.toString() || ''

      if (!strValue.trim()) return true

      switch (colType) {
        case ColumnTypes.Email:
          return isEmail(strValue)
        case ColumnTypes.Phone:
          return phonePattern.test(strValue)
        case ColumnTypes.Coordinate:
          return isValidCoordinate(strValue)
        default:
          return true
      }
    }
  }, [columnTypeMap, glideColumns])

  const handleCellActivated = useCallback(
    (cell: Item) => {
      const [col, row] = cell
      const column = glideColumns[col]
      if (column.id === '_id' || column.id === 'session-id') {
        navigator.clipboard.writeText(rows[row]._id).then(() => {
          toast.success(t('Row ID copied to clipboard'))
        })
      }
      handleGridFocus()
    },

    [glideColumns, handleGridFocus, rows, t]
  )

  /**
   * Header double-click -> open inline text input to rename
   */
  const handleHeaderClicked = useCallback(
    (colIdx: number, event: any) => {
      console.log('handleHeaderClicked', colIdx, glideColumns[colIdx])
      if (!event?.isDoubleClick) return
      event?.preventDefault?.()

      const col = glideColumns[colIdx]
      if (!col) return

      if (isSnapshot) return

      if ((col.title || '').toLowerCase() === 'timestamp') return

      const currentColumn = combinedColumns.find((c) => c.id === col.id) as ColumnAdapter | undefined
      if (!currentColumn) return
      if (isQuery && currentColumn.isSourceColumn) return
      // disallow renaming by stable semantics (type)
      if (
        currentColumn.type === ColumnTypes.RowID ||
        currentColumn.type === ColumnTypes.SessionID ||
        currentColumn.type === ColumnTypes.Email
      )
        return

      setHeaderEdit({
        colIdx,
        colId: currentColumn.id,
        value: currentColumn.name,
        bounds: event?.bounds,
      })
    },
    [glideColumns, combinedColumns, isSnapshot, isQuery]
  )

  /**
   * Calculated the grid height for lower rows count.
   * Since the bottom scroll bar is visible, we need to add the height of the scroll bar to the grid height.
   */
  const gridHeight = useMemo(() => {
    switch (true) {
      case rows.length === 0:
        return isQuery ? 105 + 40 : 105
      case rows.length === 1:
        return isQuery ? 120 + 40 : 120
      case rows.length > 1: {
        const height = 120 + ROW_HEIGHT * (rows.length - 1)
        return isQuery ? height + 40 : height
      }
      case rows.length > 10:
        return undefined
      default:
        return undefined
    }
  }, [isQuery, rows.length])

  return (
    <div ref={gridWrapperRef} className='relative flex grid-wrapper pb-[57px]'>
      <Suspense
        fallback={
          <div className='absolute inset-0 z-1 mk-bg bg-black flex items-center justify-center'>
            <span className='loading spin' />
          </div>
        }
      >
        <DataEditor
          ref={dataEditorRef}
          rows={rows?.length}
          rowMarkerStartIndex={pagination.page * pagination.perPage + 1}
          width='100%'
          columns={glideColumns}
          getCellContent={handleGetCellContent}
          rowSelect={isSnapshot ? 'none' : 'multi'}
          rowSelectionMode='multi'
          rowMarkers={isSnapshot ? 'number' : 'both'}
          customRenderers={CustomGridRenderers}
          onColumnResize={handleResizeColumn}
          onHeaderMenuClick={handleHeaderMenuClick}
          onHeaderClicked={handleHeaderClicked}
          theme={currentTheme}
          validateCell={validateInput}
          smoothScrollX={true}
          smoothScrollY={true}
          freezeColumns={freezeColumnsLength}
          headerIcons={headerIcons}
          getCellsForSelection={true}
          gridSelection={selection}
          onGridSelectionChange={setSelection}
          onPaste={true}
          onColumnMoved={handleReorderColumn}
          onCellClicked={handleCellClicked}
          onVisibleRegionChanged={handleVisibleRegionChanged}
          onCellEdited={handleEditCell}
          onCellActivated={handleCellActivated}
          onRowAppended={handleAddRow}
          trailingRowOptions={{
            sticky: true,
            tint: true,
            hint: isSnapshot ? undefined : loadingAddRow ? t('Loading...') : t('Add New Row'),
          }}
          getRowThemeOverride={getRowThemeOverride}
          onItemHovered={onItemHovered}
          rightElement={!activeTable?.isSnapshot && <GridAddColumnMenu />}
          rightElementProps={{
            fill: false,
            sticky: true,
          }}
          // showSearch={isShowSearch}
          // onSearchClose={() => setIsShowSearch(false)}
          // onKeyDown={(e) => {
          //   // Open search modal on Ctrl + F (windows) or Cmd + F (mac)
          //   if ((e.ctrlKey || e.metaKey) && e.key === 'f') {
          //     e.preventDefault()
          //     setIsShowSearch(true)
          //   }
          // }}
          height={gridHeight}
          getGroupDetails={(g) => ({
            name: g,
            icon:
              g === 'Source Columns'
                ? GridColumnIcon.HeaderCode
                : g === 'Original Columns'
                  ? GridColumnIcon.HeaderCode
                  : undefined,
          })}
          onGroupHeaderClicked={(colIdx, event) => {
            event.preventDefault()
          }}
        />
      </Suspense>

      {headerEdit &&
        (() => {
          const wrapperRect = gridWrapperRef.current?.getBoundingClientRect()
          const left = (headerEdit.bounds?.x || 0) - (wrapperRect?.left || 0) + 1
          const top = (headerEdit.bounds?.y || 0) - (wrapperRect?.top || 0) + 1
          const width = Math.max((headerEdit.bounds?.width || 160) - 2, 80)
          const height = Math.max((headerEdit.bounds?.height || 28) - 2, 24)

          return (
            <input
              ref={headerInputRef}
              defaultValue={headerEdit.value}
              onBlur={(e) => commitHeaderRename(e.currentTarget.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  commitHeaderRename((e.target as HTMLInputElement).value)
                } else if (e.key === 'Escape') {
                  e.preventDefault()
                  setHeaderEdit(null)
                }
              }}
              style={{
                position: 'absolute',
                left,
                top,
                width,
                height,
                zIndex: 20,
              }}
              className='mk-bg px-2 py-1 rounded-sm border border-slate-300 dark:border-slate-600 outline-none text-sm'
            />
          )
        })()}

      <GridModals />
      <GridColumnMenu />
      <GridSearchV2 />

      {table ? (
        <GridPagination
          pagination={pagination}
          onChange={handlePaginationChange}
          totalPages={totalPages}
          totalRows={filteredRowsCount}
          tableID={table.id}
        />
      ) : null}

      {isLoading && (
        <div className='absolute inset-0 z-1 mk-bg bg-black flex items-center justify-center'>
          <span className='loading spin' />
        </div>
      )}
    </div>
  )
}
